import { useState } from "react";
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Box, Button, Typography, styled } from "@mui/material";
import { Stack, Stepper, Step, StepLabel } from "@mui/material";
import { StepIconProps } from "@mui/material/StepIcon";

import CheckIcon from "@mui/icons-material/Check";

import Toaster from "../../component/Toaster";
import { DateTimeUtil } from "../../util/DateTimeUtils";
import ActionButton from "../../component/ActionButton";
import StepperConnector from "../../component/StepperConnector";
import {
  CommonUtil,
  FormValidation,
  LocalStorage,
  LoginUserDetails,
  Product,
} from "../../util/Utils";

import {
  StudentMasterProps,
  studentMasterService,
} from "../../store/studentMaster/StudentMasterSlice";
import { useAppDispatch } from "../../store/hooks";

import StudentInfo from "./studentInfo";
import ParentInfo from "./ParentInfoStepper";
import CourseBatchInformation from "./CourseBatchInformation";
import InvoicePayment from "./InvoiceAndPayment";
import GlobalLoader from "../../component/GlobalLoader";

interface IRegisterStudentProps {
  handleClose?: any;
  handleOpen?: any;
  userAction?: any;
  handleOpenStudentModal?: any;
  registrationTypeOptions?: any;
  levelStatusOptions?: any;
  sourceOfEnquiryOptions?: any;
  refferedByOptions?: any;
  courseNameOptions?: any;
  ciNameOptions?: any;
  batchNameOptions?: any;
  modeOfPaymentOptions?: any;
  primaryBenefitOptions?: any;
  promoCodeOptions?: any;
  countryOptions?: any;
  newStudentCreated?: any;
  lead?: any;
}

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[700] : "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 32,
  height: 24,
  display: "flex",
  borderRadius: "11px",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.completed && {
    backgroundColor: Product.ColorCode(),
  }),
  ...(ownerState.active && {
    backgroundColor: Product.ColorCode(),
  }),
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  const icons: { [index: string]: React.ReactElement } = {
    1: <CheckIcon />,
    2: <CheckIcon />,
    3: <CheckIcon />,
    4: <CheckIcon />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

const RegisterNewStudent = (props: IRegisterStudentProps) => {
  const dateFormat = process.env.REACT_APP_DEFAULT_DATE_FORMAT;
  const { promoCodeOptions, countryOptions, lead } = props;
  const { registrationTypeOptions, levelStatusOptions } = props;
  const { refferedByOptions, courseNameOptions, ciNameOptions } = props;
  const { batchNameOptions, modeOfPaymentOptions } = props;
  const { primaryBenefitOptions, sourceOfEnquiryOptions } = props;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [regKitId, setRegKitId] = useState<any>();
  const [levelDetails, setLevelDetails] = useState<any>();
  const [spinner, setSpinner] = useState<boolean>(false);
  const [isDisable, setDisable] = useState<any>({});

  const [initialValue, setInitialValue] = useState<StudentMasterProps>({
    studentName: lead ? lead.studentName : "",
    dateOfBirth: null,
    gender: "",
    schoolId: lead ? lead.schoolId : undefined,
    className: lead ? lead.className : "",
    sourceOfEnquiryId: lead ? lead.sourceOfEnquiryId : "", // sourceOfEnquiryOptions[0].value,
    sourceOfEnquiry: lead ? lead.sourceOfEnquiry : "", // sourceOfEnquiryOptions[0].label,
    referredById: lead ? lead.referredById : "",
    otherInfo: lead ? lead.otherInfo : "",
    parentName: lead ? lead.parentName : "",
    primaryMobileNumber: lead ? lead.primaryMobileNumber : "",
    whatsappMobileNumber: lead ? lead.whatsappMobileNumber : "",
    alternateMobileNumber: "",
    email: lead ? lead.email : "",
    primaryBenefitYouExpectId: "",
    courseId: lead ? lead.courseId : courseNameOptions[0].value,
    registeredDate: dayjs(Date.now()),
    courseInstructorId: "",
    batchId: "",
    levelStartDate: null,
    levelEndDate: null,
    levelStatusId: levelStatusOptions[0].value,
    promoCodeId: "",
    modeOfPaymentId: modeOfPaymentOptions?.find(
      (mod: any) => mod.label === "UPI"
    )?.value,
    discountIfAny: "",
    totalAmountToBePaid: "",
    registrationType: registrationTypeOptions[0].name,
    registrationTypeId: registrationTypeOptions[0].id,
    levelOfAdmissionId: undefined,
    receiptDate: dayjs(Date.now()),
    countryId: undefined,
    pincode: undefined,
    state: "",
    city: "",
    address1: "",
    address2: "",
    enquiryDate: lead
      ? dayjs(DateTimeUtil.format(lead.enquiryDate, dateFormat, true))
      : dayjs(Date.now()),
    parentPaidDate: dayjs(Date.now()),
    active: "Yes",
    franchiseeId: Number(LoginUserDetails.getUserDetails().franchiseeId),
    parentGstNumber: "",
    tuitionFeesChecked: "Unchecked",
    oneTimeRegistrationFeeChecked: "Unchecked",
    tuitionFeesType: "",
    registrationKitId: "",
    feeMonth: [],
    invoiceAmount: "",
    invoiceAmountAfterDiscount: 0,
  });

  const handleChangeInitialValue = (values: any) => {
    setInitialValue(values);
  };

  const [formvalues, setFormvalues] = useState<any>();
  const [feesPerMonthValidation, setFeesPerMonthValidation] =
    useState<boolean>(false);

  // ------------- Studend Info Form Validation -----------
  const studentInfo = Yup.object()
    .shape({
      studentName: Yup.string()
        .required(`${t("studentName")} ${t("is")} ${t("required")}`)
        .matches(
          FormValidation.getNamePattern(),
          `${t("studentName")} ${t("is")} ${t("incorrect")}`
        ),
      schoolId: Yup.string().required(
        `${t("schoolName")} ${t("is")} ${t("required")}`
      ),
      gender: Yup.string().required(
        `${t("gender")} ${t("is")} ${t("required")}`
      ),
      dateOfBirth: Yup.string().required(
        `${t("dateOfBirth")} ${t("is")} ${t("required")}`
      ),
      sourceOfEnquiryId: Yup.string().required(
        `${t("sourceOfEnquiry")} ${t("is")} ${t("required")}`
      ),
    })
    .test((obj: any) => {
      if (
        (sourceOfEnquiryOptions.find((soe: any) => soe.label === "Others")
          ?.value === Number(obj.sourceOfEnquiryId) ||
          refferedByOptions.find((soe: any) => soe.label === "Others")
            ?.value === Number(obj?.referredById)) &&
        !obj?.otherInfo
      ) {
        return new Yup.ValidationError(
          `${t("otherInfo")} ${t("is")} ${t("required")}`,
          null,
          "otherInfo"
        );
      } else {
        return true;
      }
    });
  // ------------- Parent Info Form Validation -----------
  const parentInfo = Yup.object()
    .shape({
      parentName: Yup.string()
        .required(`${t("parentName")} ${t("is")} ${t("required")}`)
        .matches(
          FormValidation.getNamePattern(),
          `${t("parentName")} ${t("is")} ${t("incorrect")}`
        ),
      primaryMobileNumber: Yup.string().required(
        `${t("primaryMobileNumber")} ${t("is")} ${t("required")}`
      ),
      // .matches(
      //   FormValidation.getMobileNumberPattern(),
      //   `${t("primaryMobileNumber")} ${t("is")} ${t("incorrect")}`
      // ),
      whatsappMobileNumber: Yup.string().required(
        `${t("whatsAppMobileNumber")} ${t("is")} ${t("required")}`
      ),
      email: Yup.string().matches(
        FormValidation.getEmailPattern(),
        `${t("email")} ${t("is")} ${t("incorrect")}`
      ),
      primaryBenefitYouExpectId: Yup.string().required(
        `${t("primaryBenefitYouExpact")} ${t("is")} ${t("required")}`
      ),
      // pincode: Yup.string()
      //   .required(`${t("pinCode")} ${t("is")} ${t("required")}`)
      //   // .min(5, `${t("pinCode")} ${t("is")} ${t("incorrect")}`)
      //   .min(6, `${t("pinCode")} ${t("is")} ${t("incorrect")}`)
      //   .max(6, `${t("pinCode")} ${t("is")} ${t("incorrect")}`),
    })
    .test((obj: any, Yup: any) => {
      // console.log(obj, "form values", Yup);
    });
  // ------------- course batch Info Form Validation -----------
  const courseBatchInfo = Yup.object().shape({
    courseId: Yup.string().required(
      `${t("selectCourseName")} ${t("is")} ${t("required")}`
    ),
    registeredDate: Yup.string().required(
      `${t("registeredDate")} ${t("is")} ${t("required")}`
    ),
    levelOfAdmissionId: Yup.string().required(
      `${t("SelectLevelOfAdmission")} ${t("is")} ${t("required")}`
    ),
    courseInstructorId: Yup.string().required(
      `${t("ci")} ${t("is")} ${t("required")}`
    ),
    levelStartDate: Yup.string().required(
      `${t("levelStartDate")} ${t("is")} ${t("required")}`
    ),
  }); // ------------- invoice payment Info Form Validation -----------
  const invoicePaymentInfo = Yup.object()
    .shape({
      modeOfPaymentId: Yup.string().required(
        `${t("modeOfPayment")} ${t("is")} ${t("required")}`
      ),
      // registrationKitId: Yup.string().required(
      //   `${t("registrationKitId")} ${t("is")} ${t("required")}`
      // ),
      // totalAmountToBePaid: Yup.string().required(
      //   `${t("totalAmountToBePaid")} ${t("is")} ${t("required")}`
      // ),
    })
    .test((obj: any) => {
      if (
        (Number(obj.invoiceAmountAfterDiscount) >= Number(obj.totalAmountToBePaid) &&
          Number(obj.totalAmountToBePaid) > 0) ||
        (courseNameOptions
          .find((course: any) => course.value === obj.courseId)
          .label === "Global Art")) {
        return true;
      } else {
        if (
          obj.totalAmountToBePaid === "" ||
          obj.totalAmountToBePaid === undefined
        ) {
          return new Yup.ValidationError(
            `${t("totalAmountToBePaid")} ${t("is")} ${t("required")}`,
            null,
            "totalAmountToBePaid"
          );
        } else {
          return new Yup.ValidationError(
            `${t("totalAmountToBePaidValidation")} Min 1 Max ${Number(
              obj.invoiceAmountAfterDiscount
            )}`,
            null,
            "totalAmountToBePaid"
          );
        }
      }
    });

  const steps = [
    {
      label: `${t("studentInfo")}`,
      validation: studentInfo,
    },
    {
      label: `${t("parentInfo")}`,
      validation: parentInfo,
    },
    {
      label: `${t("courseBatchInfo")}`,
      validation: courseBatchInfo,
    },
    {
      label: `${t("invoicePayment")}`,
      validation: invoicePaymentInfo,
    },
  ];

  const handleNext = (values: any) => {
    setFormvalues(values);
    if (activeStep === 0) setActiveStep((prevActiveStep) => prevActiveStep + 1);
    if (activeStep === 1) setActiveStep((prevActiveStep) => prevActiveStep + 1);
    if (activeStep === 2) setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCancel = () => {
    props?.handleClose();
    setActiveStep(1);
  };

  const levelAdmissionFetched = (levelDetails: any) => {
    if (levelDetails.status === 200) {
      levelDetails?.data.data?.forEach((item: any, index: number) => {
        if (index === 0) {
          item.selected = true;
        } else {
          item.selected = false;
        }
      });

      setRegKitId(levelDetails?.data.data.id);
    }
    setLevelDetails(levelDetails?.data.data);
  };
  const [levelOfAdmissionflag, setLevelOfAdmissionFlag] = useState(false);
  const [registrationKitId, setRegistrationKitId] = useState<any>("");
  const levelOfAdmissionChanged = () => {
    setLevelOfAdmissionFlag(true);
    setRegistrationKitId("");
  };
  const regkitItemchanged = (id: number) => {
    setRegistrationKitId(id);
  };
  const handleFormSubmit = (values: any) => {
    setSpinner(true);
    dispatch(
      studentMasterService({
        formValues: apiPreProcess(values),
        apiAction: "create",
      })
    )
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          props.newStudentCreated(result?.payload);
          Toaster.success(undefined, result.payload.data.message);
          props.handleClose();
          LocalStorage.remove("studentDropdownData");
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        console.log(err); //This console for debug the app if any error occurred in DEV
      })
      .finally(() => {
        setSpinner(false);
      });
  };

  const apiPreProcess = (values: any) => {
    values = CommonUtil.copyObject(values);

    // For Registration through lead
    values.leadId = lead ? lead.id : null;

    if (values.dateOfBirth)
      values.dateOfBirth = DateTimeUtil.format(values?.dateOfBirth, dateFormat);
    if (values.registeredDate)
      values.registeredDate = DateTimeUtil.format(
        values?.registeredDate,
        dateFormat
      );
    if (values.levelStartDate)
      values.levelStartDate = DateTimeUtil.format(
        values?.levelStartDate,
        dateFormat
      );
    if (values.receiptDate)
      values.receiptDate = DateTimeUtil.format(values?.receiptDate, dateFormat);
    if (values.enquiryDate)
      values.enquiryDate = DateTimeUtil.format(values?.enquiryDate, dateFormat);
    if (values.parentPaidDate)
      values.parentPaidDate = DateTimeUtil.format(
        values?.parentPaidDate,
        dateFormat
      );
      values.totalAmountToBePaid = values.totalAmountToBePaid || 0;
      values.tuitionFeesChecked =  (Number(values.registrationTypeId) === registrationTypeOptions[1].id &&
      LoginUserDetails.getCountry() === "India") ? 'Checked' : values.tuitionFeesChecked;
    delete values.courseInstructor;
    delete values.invoiceAmountAfterDiscount;
    return values;
  };
  const handleTutionFeesPerMonth = (feesData: any) => {
    
    if (feesData?.tuitionFeesChecked === "Checked") {
      setFeesPerMonthValidation(
        feesData?.tuitionFeesChecked === "Checked" &&
          feesData?.feeMonth.length > 0
          ? false
          : true
      );
    } else {
      setFeesPerMonthValidation(false);
    }
  };
  const checkValidation = (formValues: any, errorsKeys: any) => {
    // product is "SIP Abacus" and registrationType is "New" and brandPromiseScore is "null"
    // then next button disable
    setFeesPerMonthValidation(false);
    if (
      activeStep === 0 &&
      (!formValues.studentName ||
        !formValues.dateOfBirth ||
        !formValues.schoolId ||
        !formValues.sourceOfEnquiryId ||
        !formValues.gender ||
        (sourceOfEnquiryOptions.find((soe: any) => soe.label === "Reference")
          ?.value === Number(formValues.sourceOfEnquiryId) &&
          !formValues.referredById) ||
        (refferedByOptions.find((soe: any) => soe.label === "Others")?.value ===
          Number(formValues?.referredById) &&
          !formValues.otherInfo) ||
        (sourceOfEnquiryOptions.find((soe: any) => soe.label === "Others")
          ?.value === Number(formValues?.sourceOfEnquiryId) &&
          !formValues.otherInfo) ||
        //If brandPromiseScore is mandatory then we need to include below line
        // (Number(LoginUserDetails.getUserDetails().productId === 1) &&
        //   parseInt(formValues.registrationTypeId) ===
        //     registrationTypeOptions[0].id &&
        //   !formValues.brandPromiseScore) ||
        errorsKeys.indexOf("studentName") > -1)
    ) {
      return true;
    }
    if (
      activeStep === 1 &&
      (!formValues.parentName ||
        !formValues.primaryMobileNumber ||
        !formValues.whatsappMobileNumber ||
        !formValues.primaryBenefitYouExpectId ||
        (courseNameOptions
          .find((course: any) => course.value === formValues.courseId)
          .label.indexOf("International") < 0 &&
          LoginUserDetails.getCountry() === "India" &&
          !formValues.pincode) ||
        errorsKeys.indexOf("parentName") > -1 ||
        errorsKeys.indexOf("primaryMobileNumber") > -1 ||
        errorsKeys.indexOf("whatsappMobileNumber") > -1 ||
        // errorsKeys.indexOf("pincode") > -1 ||
        errorsKeys.indexOf("email") > -1 ||
        Object.keys(isDisable).length > 0)
    ) {
      return true;
    }

    if (
      activeStep === 2 &&
      (!formValues.levelOfAdmissionId ||
        !formValues.courseInstructorId ||
        !formValues.levelStartDate)
    ) {
      return true;
    }
    if (activeStep === 3 && !formValues.totalAmountToBePaid) {
      return true;
    } else {
      return false;
    }
  };

  const registrationKitValidation = (values: any) => {
    if (
      LoginUserDetails.getCountry() === "India" &&
      values.registrationType === "New" &&
      values.registrationKitId === ""
    ) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Stack sx={{ width: "100%" }} spacing={4}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        connector={<StepperConnector />}
      >
        {steps.map((label, i) => (
          <Step key={i}>
            <StepLabel StepIconComponent={ColorlibStepIcon}>
              {label.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      {activeStep !== steps.length && (
        <Formik
          initialValues={initialValue}
          validationSchema={steps[activeStep]?.validation}
          enableReinitialize={true}
          onSubmit={() => {}}
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            dirty,
            setFieldValue,
          }) => {
            values.registrationKitId = registrationKitId;
            console.log("errors: ", errors); //Need this line to debug if anu issue comes
            //console.log("values: ", values);
            return (
              <Form>
                <Typography sx={{ mt: 2, mb: 1 }}>
                  {activeStep === 0 && (
                    <StudentInfo
                      values={values}
                      handleBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleChangeInitialValue={handleChangeInitialValue}
                      refferedByOptions={refferedByOptions}
                      sourceOfEnquiryOptions={sourceOfEnquiryOptions}
                      registrationTypeOptions={registrationTypeOptions}
                      courseNameOptions={courseNameOptions}
                    />
                  )}
                  {activeStep === 1 && (
                    <ParentInfo
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      countryOptions={countryOptions}
                      courseNameOptions={courseNameOptions}
                      primaryBenefitOptions={primaryBenefitOptions}
                      setDisable={setDisable}
                      isDisable={isDisable}
                    />
                  )}
                  {activeStep === 2 && (
                    <CourseBatchInformation
                      values={values}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      ciNameOptions={ciNameOptions?.filter(
                        (ci: any) => ci.active === "Yes"
                      )}
                      batchNameOptions={batchNameOptions}
                      courseNameOptions={courseNameOptions}
                      levelStatusOptions={levelStatusOptions}
                      levelAdmissionFetched={levelAdmissionFetched}
                      levelOfAdmissionChanged={levelOfAdmissionChanged}
                    />
                  )}
                  {activeStep === 3 && (
                    <InvoicePayment
                      values={values}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      promoCodeOptions={promoCodeOptions}
                      modeOfPaymentOptions={modeOfPaymentOptions}
                      levelDetails={levelDetails}
                      handleTutionFeesPerMonth={handleTutionFeesPerMonth}
                      levelOfAdmissionflag={levelOfAdmissionflag}
                      regkitItemchanged={regkitItemchanged}
                      courseNameOptions={courseNameOptions}
                      registrationTypeOptions={registrationTypeOptions}
                    />
                  )}
                </Typography>
                <Box
                  className="flex"
                  sx={{
                    flexDirection: "row",
                    pt: 2,
                    justifyContent: "end",
                  }}
                >
                  {activeStep === 0 ? (
                    <Button sx={{ mr: 1 }} onClick={handleCancel}>
                      <ActionButton
                        label={"cancel"}
                        type="button"
                        actionButtonStyle="outline"
                        disabled={activeStep === 0}
                        onClick={handleCancel}
                      ></ActionButton>
                    </Button>
                  ) : (
                    <Button sx={{ mr: 1 }}>
                      <ActionButton
                        onClick={handleBack}
                        type="button"
                        label={"Previous"}
                        actionButtonStyle="outline"
                      ></ActionButton>
                    </Button>
                  )}

                  {activeStep === steps.length - 1 ? (
                    <div className="center">
                      <ActionButton
                        label={"submit"}
                        type="button"
                        disabled={
                          activeStep === 0 ||
                          Object.keys(errors).length > 0 ||
                          registrationKitValidation(values) ||
                          feesPerMonthValidation
                        }
                        onClick={() => handleFormSubmit(values)}
                      ></ActionButton>
                    </div>
                  ) : (
                    <div className="center">
                      <ActionButton
                        onClick={() => handleNext(values)}
                        type="button"
                        disabled={checkValidation(values, Object.keys(errors))}
                        label={"Next"}
                      />
                    </div>
                  )}
                </Box>
              </Form>
            );
          }}
        </Formik>
      )}
      <GlobalLoader loading={spinner} />
    </Stack>
  );
};

export default RegisterNewStudent;
